import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import CardDropdown from "components/common/CardDropdown";
import { useAdvanceTable } from "hooks";
import useClientIDs from "hooks/clientIds/useClientIds";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { Alert, Dropdown, Spinner, Col, Row } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { ClientIDDetailsDrawer } from "./ClientIDDetailsDrawer";
import { toast } from "react-toastify";
import useFormattedDate from "hooks/useFormatDate";
import { Input } from "antd";
import { Empty, Select } from "antd";
import paths from "routes/paths";

const TAB_CONFIG = [
  { key: "ALL", label: "\u{1F310} All Client IDs" },
  { key: "ACTIVE", label: "\u2705 ACTIVE" },
  { key: "RATE_LIMITED", label: "\u23F1\uFE0F RATE_LIMITED" },
  { key: "THROTTLED", label: "\u{1F422} THROTTLED" },
  { key: "SUSPENDED", label: "\u23F8\uFE0F SUSPENDED" },
  { key: "BLOCKED", label: "\u26D4 BLOCKED" },
];

export const ClientIDs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();
  const [activeTab, setActiveTab] = useState("ALL");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDrawerInEditMode, setStartDrawerInEditMode] = useState(
    Boolean(location.state?.edit)
  );
  const isSearchRequest = searchTerm.trim().length >= 3;
  const isDrawerOpen = Boolean(clientId);

  const {
    clientIDs,
    loading,
    fetchClientIDById,
    searchClients,
    selectedID,
    updateClientID,
    error,
    totalPages,
    totalElements,
  } = useClientIDs(page, pageSize, activeTab, searchTerm);

  const handleViewClientID = (id) => {
    setStartDrawerInEditMode(false);
    navigate(paths.clientIdDetails(id));
  };

  useEffect(() => {
    if (searchTerm.length > 0 && searchTerm.length < 3) {
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      searchClients(searchTerm, activeTab);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, activeTab, searchClients]);

  useEffect(() => {
    if (!isDrawerOpen || !clientId) return;

    fetchClientIDById(clientId).catch((err) => {
      toast.error(err?.response?.data?.message || "Failed to fetch client ID details", {
        theme: "colored",
      });
      navigate(paths.clientIds, { replace: true });
    });
  }, [isDrawerOpen, clientId, fetchClientIDById, navigate]);

  useEffect(() => {
    if (!isDrawerOpen) {
      setStartDrawerInEditMode(false);
      return;
    }
    setStartDrawerInEditMode(Boolean(location.state?.edit));
  }, [isDrawerOpen, location.state]);

  const handleCloseDrawer = () => {
    setStartDrawerInEditMode(false);
    navigate(paths.clientIds);
  };

  const handleTabChange = (tabKey) => {
    setPage(0);
    setActiveTab(tabKey);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(0);
  };

  const formatKey = (key) => `${key.slice(0, 12)}...${key.slice(-6)}`;
  const { formatDateTime } = useFormattedDate();
  const pillBase = {
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1.2,
    border: "1px solid",
    cursor: "default",
    display: "inline-block",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };

  const getStatusStyle = (status) => {
    const statusStyles = {
      ACTIVE: { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" },
      NEW: { bg: "#e3f2fd", text: "#1565c0", border: "#bbdefb" },
      BLOCKED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      SUSPENDED: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
      INACTIVE: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
      RATE_LIMITED: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      THROTTLED: { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" },
      DELETED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
    };

    const style = statusStyles[status] || statusStyles.DEFAULT;
    return {
      ...pillBase,
      backgroundColor: style.bg,
      color: style.text,
      borderColor: style.border,
    };
  };

  const columns = [
    {
      accessorKey: "id",
      header: "Client ID",
      cell: ({ getValue }) => (
        <h6 className="mb-0 text-900 fs-10">
          {formatKey(getValue())}
        </h6>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "apiKeyId",
      header: "API Key",
      cell: ({ getValue }) => (
        <span className="text-500 d-inline-block" style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}>
          {formatKey(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = String(getValue() || "UNKNOWN").toUpperCase();
        const label = value.replaceAll("_", " ");
        return <span style={getStatusStyle(value)}>{label}</span>;
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      cell: ({ getValue }) => (
        <span className="text-500 d-inline-block" style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}>
          {formatDateTime(getValue())}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleViewClientID(item.id)}>
                View/edit
              </Dropdown.Item>
              {false && (
                      <>
                      <Dropdown.Divider as="div" />
                      <Dropdown.Item onClick={() => {}} className="text-danger">
                       Delete
                      </Dropdown.Item>
                      </>
               )}
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: clientIDs,
    columns,
    sortable: true,
    pagination: true,
    selection: true,
    perPage: 10,
  });

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading style={{ fontSize: 16 }}>
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          Error
        </Alert.Heading>
        <p style={{ fontSize: "13.33px" }}>{error}</p>
      </Alert>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Client IDs"
        subtitle="Monitor and manage your client credentials."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <Row
          className="mb-3 align-items-end gx-3 p-2 rounded bg-light mx-auto"
          style={{ maxWidth: 980 }}
        >
          <Col md={6}>
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F50E} Search Client IDs"}
            </div>
            <Input
              placeholder='Search by "id" or "status" (min. 3 chars)...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{
                width: "63%",
                borderRadius: 24,
              }}
              suffix={
                loading && isSearchRequest ? (
                  <Spinner animation="border" size="sm" variant="success" />
                ) : (
                  searchTerm.length > 0 &&
                  searchTerm.length < 3 && (
                    <small className="text-warning" style={{ fontSize: "10px" }}>
                      Type {3 - searchTerm.length} more...
                    </small>
                  )
                )
              }
            />
          </Col>
          <Col md={3}>
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F3F7}\uFE0F Status Filter"}
            </div>
            <Select
              style={{ width: "100%" }}
              value={activeTab}
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 240 } } }}
              onChange={handleTabChange}
              options={TAB_CONFIG.map(({ key, label }) => ({
                label,
                value: key,
              }))}
            />
          </Col>
        </Row>

        {loading && clientIDs.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Spinner animation="border" variant="success" />
          </div>
        ) : clientIDs.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Empty
              description={
                searchTerm.trim()
                  ? `No client IDs with the search term "${searchTerm.trim()}" were found`
                  : `No clients found in ${activeTab} tab.`
              }
            />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-300 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={page > 0 && totalElements > 0 ? () => setPage(page - 1) : undefined}
                onNext={
                  page < totalPages - 1 && totalElements > 0
                    ? () => setPage(page + 1)
                    : undefined
                }
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      <ClientIDDetailsDrawer
        selectedID={selectedID}
        loading={loading}
        showDrawer={isDrawerOpen}
        setShowDrawer={(show) => {
          if (!show) handleCloseDrawer();
        }}
        startInEditMode={startDrawerInEditMode}
        updateClientID={updateClientID}
      />
    </>
  );
};
